/**
 * Typed search params for the `/employees` route. The text search + the
 * active/archived toggle live in the URL so they are deep-linkable AND
 * compile-checked (the reports/invoices/estimates `searchParams` pattern). TanStack
 * Router validates the raw URL params through `validateEmployeesSearch` on every
 * navigation.
 *
 * `?q=<text>` filters the list by name/contact (client-side over the coarse payload —
 * the directory is small and already fully loaded; no extra round-trip). `?show_archived=1`
 * flips the list to archived employees (legacy parity — `EmployeesPage` `showArchived`).
 * The coarse endpoint owns the active/archived split + the counts; the SPA carries the
 * flags and does the text filter in-memory.
 */

export interface EmployeesSearch {
  q?: string;
  show_archived?: boolean;
}

/** Coerce a raw URL value into a boolean (`1`/`true` → true). */
function asBool(value: unknown): boolean {
  return value === true || value === '1' || value === 'true';
}

/** Coerce a raw URL value into a trimmed, length-capped search string (else undefined). */
function asQuery(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, 100);
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * TanStack Router `validateSearch` for the employees list route. Keeps the search
 * text only when non-empty and the archived flag only when truthy, so the canonical
 * URL stays clean (`/employees` vs `/employees?q=hank` vs `/employees?show_archived=1`).
 */
export function validateEmployeesSearch(raw: Record<string, unknown>): EmployeesSearch {
  const search: EmployeesSearch = {};
  const q = asQuery(raw.q);
  if (q) search.q = q;
  if (asBool(raw.show_archived)) search.show_archived = true;
  return search;
}
